import queryString, { ParsedQuery } from 'query-string'
import {
  regexLocaleCharacters,
  replaceLocaleCharacters
} from './localeCharacterUtils'

export interface DecodedQueryObject {
  adress?: string
  arrende?: number
  avgift?: number
  biarea?: number
  boarea?: number
  byggt?: number
  drift?: number
  kommunBenamning?: string
  kommunKod?: string
  pris?: number
  tax?: number
  tomtr?: number
  typ?: number
  typBenamning?: string
  ['wT.mc_id']?: number
  nyproduktion?: boolean
  brokerid?: number
  hemnetid?: number
  image?: string
  objekttyp?: number
  postnr?: number
  supplier?: number
  utm_campaign?: string
  utm_content?: string
  utm_medium?: string
  utm_source?: string
}

type ValidStringQueryKey =
  | 'adress'
  | 'kommunBenamning'
  | 'kommunKod'
  | 'typBenamning'
  | 'image'
  | 'utm_campaign'
  | 'utm_content'
  | 'utm_medium'
  | 'utm_source'

type ValidNumericalQueryKey =
  | 'arrende'
  | 'avgift'
  | 'biarea'
  | 'boarea'
  | 'byggt'
  | 'drift'
  | 'pris'
  | 'tax'
  | 'tomtr'
  | 'typ'
  | 'wT.mc_id'
  | 'brokerid'
  | 'hemnetid'
  | 'objekttyp'
  | 'postnr'
  | 'supplier'

function isValidQueryKey(key: string) {
  if (
    key === 'adress' ||
    key === 'arrende' ||
    key === 'avgift' ||
    key === 'biarea' ||
    key === 'boarea' ||
    key === 'byggt' ||
    key === 'drift' ||
    key === 'kommunBenamning' ||
    key === 'kommunKod' ||
    key === 'pris' ||
    key === 'tax' ||
    key === 'tomtr' ||
    key === 'typ' ||
    key === 'typBenamning' ||
    key === 'wT.mc_id' ||
    key === 'brokerid' ||
    key === 'hemnetid' ||
    key === 'image' ||
    key === 'objekttyp' ||
    key === 'postnr' ||
    key === 'supplier' ||
    key === 'utm_campaign' ||
    key === 'utm_content' ||
    key === 'utm_medium' ||
    key === 'utm_source'
  )
    return true
  return false
}

function isValidNumericalQueryKey(key: string) {
  if (
    key === 'arrende' ||
    key === 'avgift' ||
    key === 'biarea' ||
    key === 'boarea' ||
    key === 'byggt' ||
    key === 'drift' ||
    key === 'pris' ||
    key === 'tax' ||
    key === 'tomtr' ||
    key === 'typ' ||
    key === 'wT.mc_id' ||
    key === 'brokerid' ||
    key === 'hemnetid' ||
    key === 'objekttyp' ||
    key === 'postnr' ||
    key === 'supplier'
  )
    return true
  return false
}

export function makeObjKeysLowerCamelCase(obj: ParsedQuery<string>) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.charAt(0).toLowerCase() + key.slice(1),
      value
    ])
  )
}

export function getValueShouldBeProcessed(
  key: string,
  value: string | (string | null)[] | null
) {
  // Do not process invalid keys:
  if (!isValidQueryKey(key)) return null
  // Do not process invalid values:
  // Do not use entries with null values or empty strings
  if (value === null || value === '') return null
  // Do not use entries with arrays as values
  if (Array.isArray(value)) return null
  // Remove image props with '0' value
  if (key === 'image' && value === '0') return null
  return value
}

export function decodeQueryObject(queryObject: ParsedQuery<string>) {
  const lowerCamelCaseQueryObject = makeObjKeysLowerCamelCase(queryObject)
  const decodedQueryObject: DecodedQueryObject = {}
  for (const key in lowerCamelCaseQueryObject) {
    const value = getValueShouldBeProcessed(key, lowerCamelCaseQueryObject[key])
    if (!value) continue
    // Remove spaces from query that some partners send
    const valueWithoutSpaces = value.replaceAll('&nbsp;', '')
    // Convert booleans
    if (key === 'nyproduktion')
      decodedQueryObject[key] = value === 'true' ? true : false
    // Add special handling for avgift=null or drift=null
    if (
      (key === 'avgift' || key === 'drift') &&
      valueWithoutSpaces === 'null'
    ) {
      decodedQueryObject[key as ValidNumericalQueryKey] = 0
      continue
    }
    // If it can be converted to number do it
    if (isValidNumericalQueryKey(key) && !isNaN(+valueWithoutSpaces)) {
      decodedQueryObject[key as ValidNumericalQueryKey] = Number(
        +valueWithoutSpaces
      )
      continue
    }
    regexLocaleCharacters.lastIndex = 0
    if (regexLocaleCharacters.test(valueWithoutSpaces)) {
      try {
        decodedQueryObject[key as ValidStringQueryKey] = decodeURIComponent(
          replaceLocaleCharacters(valueWithoutSpaces)
        )
      } catch (error) {
        // Do nothing
      }
    } else {
      decodedQueryObject[key as ValidStringQueryKey] = valueWithoutSpaces
    }
  }
  return decodedQueryObject
}

export function getQueryObjectFromWindow(windowLocationSearch: string) {
  const parsedQueryObj = queryString.parse(windowLocationSearch)
  const decodedCamelCaseQueryObject = decodeQueryObject(parsedQueryObj)
  return decodedCamelCaseQueryObject
}

export function loadQuery() {
  const windowLocationSearch = window.location.search
  const decodedCamelCaseQueryObject =
    getQueryObjectFromWindow(windowLocationSearch)
  if (Object.keys(decodedCamelCaseQueryObject).length <= 0) return null
  return decodedCamelCaseQueryObject
}

export const getAccommodationClassification = (classification = 1) => {
  const villa = 1
  const condominium = 2
  const recreationalRealEstate = 3
  const condominiumClassifications = [130, 400, 410, 420, 430, 440, 450, 330]
  const recreationalRealEstateClassifications = [210, 200]

  if (condominiumClassifications.includes(classification)) {
    return condominium
  }

  if (recreationalRealEstateClassifications.includes(classification)) {
    return recreationalRealEstate
  }

  return villa
}
